import {
  AppWindow,
  Settings,
  ArrowLeftRight,
  ShieldCheck,
  ClipboardList,
  Cpu,
  FileSpreadsheet,
  FileUp,
  Gauge,
  KeyRound,
  LayoutDashboard,
  Laptop,
  MonitorSmartphone,
  Package,
  Settings2,
  User,
  UserCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { Capability } from '@skhub/contracts';

/** `System` items are not listed in the groups: they sit at the foot of the sidebar (Settings). */
export type NavGroup = 'Overview' | 'Hardware' | 'Software' | 'Operations' | 'Personal' | 'System';

/**
 * Hardware and Software & licences are two divisions of the same platform: same visual system,
 * separate groups, separate iconography (physical devices vs keys and apps).
 */
export const NAV_GROUPS: { key: NavGroup; label: string; icon: LucideIcon }[] = [
  { key: 'Overview', label: 'Overview', icon: Gauge },
  { key: 'Hardware', label: 'Hardware', icon: Cpu },
  { key: 'Software', label: 'Software & licences', icon: AppWindow },
  { key: 'Operations', label: 'Operations', icon: Settings2 },
  { key: 'Personal', label: 'Personal', icon: User },
];

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  group: NavGroup;
  capability: Capability;
  shortcut?: string;
  keywords?: string;
  /** Match the path exactly (for section roots that have child routes of their own). */
  end?: boolean;
}

/** Single source for sidebar, command palette and "g x" shortcuts. Filtered by capability. */
export const NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, group: 'Overview', capability: 'insights.read', shortcut: 'G D', keywords: 'home overview' },
  { to: '/laptops', label: 'Laptop Intelligence', icon: Laptop, group: 'Overview', capability: 'insights.read', shortcut: 'G L', keywords: 'laptops teams allocation' },
  { to: '/assets', label: 'Assets', icon: Package, group: 'Hardware', capability: 'inventory.read', shortcut: 'G A', keywords: 'devices hardware inventory' },
  { to: '/employees', label: 'Employees', icon: Users, group: 'Hardware', capability: 'people.read', shortcut: 'G E', keywords: 'people staff custodians' },
  { to: '/warranty', label: 'Warranty', icon: ShieldCheck, group: 'Hardware', capability: 'insights.read', shortcut: 'G W', keywords: 'warranty expiry serial vendor dell lenovo hp coverage' },
  { to: '/software', label: 'Licence insights', icon: AppWindow, group: 'Software', capability: 'insights.read', shortcut: 'G S', keywords: 'software licences licenses compliance renewals utilisation', end: true },
  { to: '/software/licenses', label: 'Licence register', icon: KeyRound, group: 'Software', capability: 'licenses.read', keywords: 'software licences licenses subscriptions microsoft 365 volume' },
  { to: '/software/seats', label: 'Licence holders', icon: UserCheck, group: 'Software', capability: 'licenses.read', keywords: 'licence holders seats assignments who has people software' },
  { to: '/import', label: 'Import', icon: FileUp, group: 'Operations', capability: 'import.run', keywords: 'excel csv upload' },
  { to: '/reports', label: 'Reports', icon: FileSpreadsheet, group: 'Operations', capability: 'reports.export', keywords: 'export xlsx', end: true },
  { to: '/reports/movements', label: 'Monthly movements', icon: ArrowLeftRight, group: 'Operations', capability: 'reports.export', keywords: 'monthly transactions assigned returned retired report handover' },
  { to: '/audit', label: 'Audit trail', icon: ClipboardList, group: 'Operations', capability: 'audit.read', keywords: 'history log events' },
  { to: '/my-devices', label: 'My devices', icon: MonitorSmartphone, group: 'Personal', capability: 'self.devices', shortcut: 'G M', keywords: 'mine personal' },
  { to: '/settings', label: 'Settings', icon: Settings, group: 'System', capability: 'settings.read', shortcut: 'G ,', keywords: 'settings recovery restore backup administrators password' },
];

/** The nav item a path belongs to: longest matching prefix, so /software/licenses/x → Licence register. */
export function navItemFor(pathname: string, items: NavItem[] = NAV): NavItem | undefined {
  return [...items].sort((a, b) => b.to.length - a.to.length).find((n) => pathname === n.to || pathname.startsWith(`${n.to}/`));
}
